import React, { useEffect, useRef, useState, useCallback } from 'react';

function parseLyrics(text) {
  const items = [];
  (text || '').split('\n').forEach((ln) => {
    const s = ln.trim();
    if (!s) return;
    if (/^\[\[.*\]\]$/.test(s)) return;
    const m = s.match(/^\[(\d+(?:\.\d+)?)?\s*:[^\]]*\]\s*(.*)$/);
    if (m) {
      const start = (m[1] != null && m[1] !== '') ? parseFloat(m[1]) : null;
      const txt = (m[2] || '').trim();
      if (txt) items.push({ start, text: txt });
    } else {
      items.push({ start: null, text: s });
    }
  });
  const n = items.length;
  let i = 0;
  while (i < n) {
    if (items[i].start == null) { i += 1; continue; }
    let j = i + 1;
    while (j < n && items[j].start == null) j += 1;
    const startT = items[i].start;
    const span = j - i;
    const endT = j < n ? items[j].start : startT + span * 3.5;
    for (let k = 0; k < span; k += 1) {
      items[i + k].start = startT + ((endT - startT) * k) / span;
    }
    i = j;
  }
  for (let k = 0; k < n; k += 1) { if (items[k].start == null) items[k].start = 0; }
  return items;
}

export default function RadioSection({ t, onClose, open }) {
  const [songs, setSongs] = useState(null);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [started, setStarted] = useState(false);
  const [curLine, setCurLine] = useState(-1);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  const audioRef = useRef(null);
  const boxRef = useRef(null);
  const recentRef = useRef([]);
  const timerRef = useRef(null);
  const stoppedRef = useRef(false);

  const COOLDOWN = 10;
  const GAP = 8;

  const lines = parseLyrics(current ? current.lyrics : '');

  useEffect(() => {
    let cancelled = false;
    fetch('/api/ai/radio', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        if (d && Array.isArray(d.songs)) setSongs(d.songs);
        else setSongs([]);
      })
      .catch(() => { if (!cancelled) setSongs([]); });
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
      const a = audioRef.current;
      if (a) { try { a.pause(); } catch (e) {} }
    };
  }, []);

  const pickNext = useCallback(() => {
    const list = songs || [];
    if (!list.length) return null;
    const cd = Math.min(COOLDOWN, list.length - 1);
    const recent = recentRef.current.slice(-cd);
    let pool = list.filter((s) => !recent.includes(s.url));
    if (!pool.length) pool = list;
    const chosen = pool[Math.floor(Math.random() * pool.length)];
    recentRef.current.push(chosen.url);
    if (recentRef.current.length > 60) recentRef.current = recentRef.current.slice(-60);
    return chosen;
  }, [songs]);

  const playSong = useCallback((song) => {
    if (!song) return;
    stoppedRef.current = false;
    setCountdown(0);
    setCurLine(-1);
    setCur(0);
    setDur(0);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setCurrent(song);
    const a = audioRef.current;
    if (!a) return;
    a.src = song.url;
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, []);

  const scheduleNext = useCallback(() => {
    if (stoppedRef.current) return;
    setPlaying(false);
    let left = GAP;
    setCountdown(left);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      left -= 1;
      if (left <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setCountdown(0);
        if (!stoppedRef.current) playSong(pickNext());
      } else {
        setCountdown(left);
      }
    }, 1000);
  }, [pickNext, playSong]);

  const start = () => { setStarted(true); playSong(pickNext()); };

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      stoppedRef.current = true;
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setCountdown(0);
      try { a.pause(); } catch (e) {}
      setPlaying(false);
      return;
    }
    if (countdown > 0) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setCountdown(0);
      playSong(pickNext());
      return;
    }
    if (!started || !current) { start(); return; }
    stoppedRef.current = false;
    a.play().then(() => setPlaying(true)).catch(() => {});
  };

  const onTime = () => {
    const a = audioRef.current;
    if (!a) return;
    setCur(a.currentTime || 0);
    if (!lines.length) return;
    const tt = a.currentTime;
    const lastStart = lines[lines.length - 1].start || 0;
    let i = -1;
    if (lastStart <= 0.01 && a.duration && isFinite(a.duration)) {
      i = Math.min(lines.length - 1, Math.floor((tt / a.duration) * lines.length));
    } else {
      for (let k = 0; k < lines.length; k += 1) { if (lines[k].start != null && lines[k].start <= tt + 0.15) i = k; }
    }
    if (i !== curLine) setCurLine(i);
  };

  const onLoaded = () => { const a = audioRef.current; if (a && isFinite(a.duration)) setDur(a.duration); };
  const onSeek = (e) => { const a = audioRef.current; const v = Number(e.target.value); if (a) a.currentTime = v; setCur(v); };

  useEffect(() => {
    const box = boxRef.current;
    if (!box || curLine < 0) return;
    const el = box.children[curLine];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [curLine]);

  const download = async () => {
    if (!current) return;
    try {
      const r = await fetch(current.url);
      const b = await r.blob();
      const u = URL.createObjectURL(b);
      const el = document.createElement('a');
      el.href = u;
      el.download = (current.word || 'song') + '.mp3';
      el.click();
      URL.revokeObjectURL(u);
    } catch (e) {
      window.open(current.url, '_blank');
    }
  };

  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
  const fmt = (s) => { s = Math.max(0, Math.floor(s || 0)); return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0'); };
  const pct = dur ? (cur / dur) * 100 : 0;

  return (
    <section className={"radio-view" + (open ? "" : " radio-hidden")}>
      <div className="radio-head">
        <h2 className="radio-title">{t('radio')}</h2>
        <button type="button" className="radio-close" aria-label="Закрыть" onClick={onClose}>×</button>
      </div>

      {songs === null && <p className="radio-info">{t('radioLoading')}</p>}
      {songs !== null && songs.length === 0 && <p className="radio-info">{t('radioEmpty')}</p>}

      {songs !== null && songs.length > 0 && (
        <div className="radio-player">
          <div className="radio-word">{current ? cap(current.disp || current.word) : '—'}</div>
          {current && current.tr && current.tr.length > 0 && (
            <div className="radio-tr">{current.tr.join(', ')}</div>
          )}
          {countdown > 0 && (
            <div className="radio-countdown">{t('radioNext')} {countdown}</div>
          )}

          <audio
            ref={audioRef}
            className="radio-audio-native"
            preload="none"
            onEnded={scheduleNext}
            onTimeUpdate={onTime}
            onLoadedMetadata={onLoaded}
            onPlay={() => setPlaying(true)}
          />

          <div className="radio-seek-row">
            <span className="radio-time">{fmt(cur)}</span>
            <input
              type="range"
              className="song-ctl-seek"
              min="0"
              max={dur || 0}
              step="0.1"
              value={cur}
              onChange={onSeek}
              aria-label="Перемотка"
              style={{ background: 'linear-gradient(to right, #8b5cf6 ' + pct + '%, rgba(139,92,246,.22) ' + pct + '%)' }}
            />
            <span className="radio-time">{fmt(dur)}</span>
          </div>

          <div className="radio-controls">
            <button type="button" className="radio-btn radio-btn-main" onClick={togglePlay}>
              {playing ? t('radioPause') : t('radioPlay')}
            </button>
            <button type="button" className="radio-btn radio-btn-dl" onClick={download} disabled={!current}>
              {t('radioDownload')}
            </button>
          </div>

          {lines.length > 0 && (
            <div className="song-lyrics radio-lyrics" ref={boxRef}>
              {lines.map((l, i) => (
                <div key={i} className={'song-line' + (i === curLine ? ' song-line-active' : '')}>{l.text}</div>
              ))}
            </div>
          )}
          {current && current.cust && current.cust.nick && (
            <div className="radio-customer">
              <span className="radio-customer-label">Заказчик:</span>
              {current.cust.avatar ? (
                <img className="radio-customer-ava" src={current.cust.avatar} alt={current.cust.nick} />
              ) : (
                <span className="radio-customer-ava radio-customer-initial">{current.cust.nick.charAt(0).toUpperCase()}</span>
              )}
              <span className="radio-customer-nick">{current.cust.nick}</span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
